import { useState, useEffect } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import "./RefundCalendarModal.css";

const RefundCalendarModal = ({ visible, onClose, onSave, initialDate }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);


  const normalizeToLocalDate = (value) => {
    if (!value) return null;
  
    const d = value instanceof Date ? value : new Date(value);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };
  useEffect(() => {
    if (!visible || !initialDate) return;
  
    const normalizedDate = normalizeToLocalDate(initialDate);
  
    setSelectedDate(normalizedDate);
    setCurrentMonth(
      new Date(
        normalizedDate.getFullYear(),
        normalizedDate.getMonth(),
        1
      )
    );
  }, [visible, initialDate]);

  // Initialize calendar on component mount and when month changes
  useEffect(() => {
    if (visible) {
      generateCalendarDays(currentMonth);
    }
  }, [currentMonth, visible]);

  // Generate calendar days for the current month view
  const generateCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Get the day of the week for the first day (0-6, 0 is Sunday)
    const firstDayOfWeek = firstDay.getDay();

    // Calculate days from previous month to display
    const daysFromPrevMonth = firstDayOfWeek;

    // Calculate how many days to show from the previous month
    const prevMonthLastDay = new Date(year, month, 0).getDate();

    const days = [];

    // Add days from previous month
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      days.push({
        day,
        month: month - 1 < 0 ? 11 : month - 1,
        year: month - 1 < 0 ? year - 1 : year,
        isCurrentMonth: false,
        date: new Date(
          month - 1 < 0 ? year - 1 : year,
          month - 1 < 0 ? 11 : month - 1,
          day
        ),
      });
    }

    // Add days from current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        day: i,
        month,
        year,
        isCurrentMonth: true,
        date: new Date(year, month, i),
      });
    }

    // Add days from next month to complete the calendar grid (6 rows x 7 columns = 42 cells)
    const totalDaysNeeded = 42; // 6 rows * 7 days
    const daysFromNextMonth = totalDaysNeeded - days.length;

    for (let i = 1; i <= daysFromNextMonth; i++) {
      days.push({
        day: i,
        month: month + 1 > 11 ? 0 : month + 1,
        year: month + 1 > 11 ? year + 1 : year,
        isCurrentMonth: false,
        date: new Date(
          month + 1 > 11 ? year + 1 : year,
          month + 1 > 11 ? 0 : month + 1,
          i
        ),
      });
    }

    setCalendarDays(days);
  };

  // Navigate to previous month
  const goToPrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  // Check if a date is selected
  const isDateSelected = (date) => {
    if (!selectedDate) return false;
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  // Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if a date is in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Format date to string
  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Handle date selection
  const handleDateSelect = (day) => {
    if (isPastDate(day.date)) return;
    setSelectedDate(day.date);
  };

  // Handle save action
  const handleSave = () => {
    if (selectedDate) {
      onSave(selectedDate);
    }
  };

  if (!visible) return null;

  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthYear = currentMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="aso-refund-modal" onClick={onClose}>
      <div
        className="aso-refund-content calendar-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="calendar-header">
          <h3>Set Refund Deadline</h3>
          <X className="calendar-close" onClick={onClose} />
        </div>

        <p className="calendar-description">
          Select a specific date as the deadline for refunds to ensure clarity
          with your guests
        </p>

        <div className="calendar-navigation">
          <button className="calendar-nav-button" onClick={goToPrevMonth}>
            <ChevronLeft />
          </button>
          <div className="calendar-current-month">{monthYear}</div>
          <button className="calendar-nav-button" onClick={goToNextMonth}>
            <ChevronRight />
          </button>
        </div>

        <div className="calendar-grid">
          {weekdays.map((day, index) => (
            <div key={`weekday-${index}`} className="calendar-weekday">
              {day}
            </div>
          ))}

          {calendarDays.map((day, index) => (
            <div
              key={`day-${index}`}
              className={`calendar-day ${
                !day.isCurrentMonth ? "calendar-other-month" : ""
              } 
                ${isDateSelected(day.date) ? "calendar-selected" : ""} 
                ${isToday(day.date) ? "calendar-today" : ""} 
                ${isPastDate(day.date) ? "calendar-past" : ""}`}
              onClick={() => handleDateSelect(day)}
            >
              {day.day}
            </div>
          ))}
        </div>

        {selectedDate && (
          <div className="calendar-selected-date">
            Selected: <strong>{formatDate(selectedDate)}</strong>
          </div>
        )}

        <div className="calendar-buttons">
          <button className="calendar-button-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="calendar-button-save"
            onClick={handleSave}
            disabled={!selectedDate}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default RefundCalendarModal;
